import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import config from '../devprodConfig';
import { useUser } from '../context/UserContext';
import {
  getLlmPrompts, getLlmPromptState, saveLlmPromptVersion, createLlmPersonalPrompt,
  setLlmPersonalPromptActive, mergeLlmPromptFromUniversal, getLlmReportTypes,
  setLlmPromptReportTypes, archiveLlmPrompt, archiveLlmPromptGroup, restoreLlmPrompt,
} from '../api/api';
import PromptRowEditor from '../components/llmPrompts/PromptRowEditor';
import PromptHistory from '../components/llmPrompts/PromptHistory';
import MergeDialog from '../components/llmPrompts/MergeDialog';
import PromptExamples from '../components/llmPrompts/PromptExamples';
import { assembleRows, missingVariables, toDraftRows, draftSignature, newRowKey } from '../components/llmPrompts/promptText';
import '../components/llmPrompts/llmPrompts.css';

const GLOBAL_KEY = '_global';
const PREFERRED_KEY = 'wk3_yhteenveto';
// Can't be deleted (the backend refuses too); every other prompt is optional for generation.
const PROTECTED_KEYS = [GLOBAL_KEY];
const TYPE_STORAGE_KEY = 'llmp-report-type';
const TAB_TO_URL = { universal: 'yleinen', personal: 'oma' };
const TAB_FROM_URL = { yleinen: 'universal', oma: 'personal' };
const CLIPBOARD_KEY = 'llmp-clipboard';

const storageGet = (key, fallback) => {
  try { const v = sessionStorage.getItem(key) ?? localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); }
  catch { return fallback; }
};
const storageSet = (key, value, session = false) => {
  try { (session ? sessionStorage : localStorage).setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
};
const errMsg = (err, fallback) => err?.response?.data?.error || fallback;

// Edits the DB-stored LLM prompts: the universal version (admins) and each
// user's personal copy. Every save creates a new version; nothing is overwritten.
export default function LlmPromptit() {
  const { meUser, localUser } = useUser();
  const currentUser = meUser || localUser;
  const isAdmin = currentUser?.rooli === 'admin';

  // The view lives in the URL so it survives a refresh and can be shared:
  //   ?tyyppi=<report type>&prompti=<prompt key>&nakyma=yleinen|oma&kayttaja=<user id>
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTabRef = useRef(TAB_FROM_URL[searchParams.get('nakyma')] ?? null);

  const [users, setUsers] = useState([]);
  const [targetUserId, setTargetUserId] = useState(() => Number(searchParams.get('kayttaja')) || null); // null = me
  const [reportTypes, setReportTypes] = useState([]);
  const [reportType, setReportType] = useState(() => searchParams.get('tyyppi') ?? storageGet(TYPE_STORAGE_KEY, ''));
  const [archived, setArchived] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(null); // { kind: 'prompt'|'group', value }
  const [addingType, setAddingType] = useState('');
  // Copied heading section (rows) to paste into another prompt; kept for the browser session.
  const [clipboard, setClipboard] = useState(() => storageGet(CLIPBOARD_KEY, null));
  const [prompts, setPrompts] = useState(null);
  const [selectedKey, setSelectedKey] = useState(() => searchParams.get('prompti') || null);
  const [state, setState] = useState(null);
  const [loadingState, setLoadingState] = useState(false);
  const [tab, setTab] = useState('universal');

  const [draftRows, setDraftRows] = useState([]);
  const [draftExcluded, setDraftExcluded] = useState([]);
  const [baseline, setBaseline] = useState('');
  const [note, setNote] = useState('');
  const [resetToken, setResetToken] = useState(0);

  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [flash, setFlash] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);
  const [mergeError, setMergeError] = useState(null);

  const isGlobal = selectedKey === GLOBAL_KEY;
  const current = tab === 'personal' ? state?.personal : state?.universal;
  const canEdit = tab === 'universal' ? isAdmin : !!state?.personal;
  const dirty = draftSignature(draftRows, draftExcluded) !== baseline;
  const targetName = targetUserId ? users.find(u => u.id === targetUserId)?.nimi : null;

  const resetDraft = useCallback((version) => {
    const rows = toDraftRows(version?.rows);
    const excluded = version?.excluded_item_ids ?? [];
    setDraftRows(rows);
    setDraftExcluded(excluded);
    setBaseline(draftSignature(rows, excluded));
    setResetToken(t => t + 1);
    setNote('');
    setConflict(false);
  }, []);

  const loadList = useCallback(async () => {
    try {
      const { data } = await getLlmPrompts(targetUserId, { reportType });
      setPrompts(data);
      setSelectedKey(key => (key && data.some(p => p.prompt_key === key)
        ? key
        : (data.find(p => p.prompt_key === PREFERRED_KEY) ?? data[0])?.prompt_key ?? null));
      if (isAdmin) {
        const res = await getLlmPrompts(targetUserId, { archived: true });
        setArchived(res.data);
      }
    } catch (err) {
      setError(errMsg(err, 'Promptien lataus epäonnistui.'));
      setPrompts([]);
    }
  }, [targetUserId, reportType, isAdmin]);

  useEffect(() => {
    getLlmReportTypes().then(res => setReportTypes(res.data)).catch(() => setReportTypes([]));
  }, []);

  useEffect(() => { storageSet(TYPE_STORAGE_KEY, reportType); }, [reportType]);

  // Keep the URL in sync with the view (replace, so the back button isn't flooded).
  useEffect(() => {
    const params = {};
    if (reportType) params.tyyppi = reportType;
    if (selectedKey) params.prompti = selectedKey;
    if (selectedKey && selectedKey !== GLOBAL_KEY) params.nakyma = TAB_TO_URL[tab];
    if (targetUserId) params.kayttaja = String(targetUserId);
    setSearchParams(params, { replace: true });
  }, [reportType, selectedKey, tab, targetUserId, setSearchParams]);

  const refresh = useCallback(async (key, keepTab) => {
    setLoadingState(true);
    setError(null);
    try {
      const { data } = await getLlmPromptState(key, targetUserId);
      setState(data);
      const urlTab = initialTabRef.current;
      initialTabRef.current = null;
      const nextTab = key === GLOBAL_KEY ? 'universal' : keepTab ?? urlTab ?? (data.in_use === 'personal' ? 'personal' : 'universal');
      setTab(nextTab);
      resetDraft(nextTab === 'personal' ? data.personal : data.universal);
    } catch (err) {
      setState(null);
      setError(errMsg(err, 'Promptin lataus epäonnistui.'));
    } finally {
      setLoadingState(false);
    }
  }, [targetUserId, resetDraft]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (selectedKey) refresh(selectedKey); }, [selectedKey, refresh]);

  useEffect(() => {
    if (!isAdmin) return;
    fetch(`${config.apiBaseUrl}/api/users`, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : []))
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));
  }, [isAdmin]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const confirmDiscard = () => !dirty || window.confirm('Sinulla on tallentamattomia muutoksia. Hylätäänkö ne?');

  const selectPrompt = (key) => {
    if (key === selectedKey || !confirmDiscard()) return;
    setShowPreview(false);
    setSelectedKey(key);
  };

  const selectTab = (next) => {
    if (next === tab || !confirmDiscard()) return;
    setTab(next);
    resetDraft(next === 'personal' ? state?.personal : state?.universal);
  };

  const selectUser = (id) => {
    if (!confirmDiscard()) return;
    setTargetUserId(id);
  };

  const validationError = useMemo(() => {
    if (!state || !canEdit) return null;
    if (draftRows.length === 0) return 'Promptissa pitää olla vähintään yksi rivi.';
    if (draftRows.some(r => !r.heading.trim() || !r.content.trim())) return 'Jokaisella rivillä pitää olla otsikko ja teksti.';
    const missing = missingVariables(draftRows, state.prompt.required_variables);
    if (missing.length) return `Pakollinen muuttuja puuttuu: ${missing.map(v => `{{${v}}}`).join(', ')}`;
    return null;
  }, [draftRows, state, canEdit]);

  const previewText = useMemo(() => {
    const globals = !isGlobal && state?.globals ? state.globals.rows.filter(r => !draftExcluded.includes(r.item_id)) : [];
    return [globals.length ? assembleRows(globals) : null, assembleRows(draftRows)].filter(Boolean).join('\n\n');
  }, [draftRows, draftExcluded, state, isGlobal]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setConflict(false);
    try {
      const { data } = await saveLlmPromptVersion(selectedKey, {
        scope: tab,
        rows: draftRows.map(r => ({ item_id: r.item_id ?? undefined, heading: r.heading, content: r.content })),
        excluded_item_ids: isGlobal ? [] : draftExcluded,
        note: note.trim() || undefined,
        base_version_id: current?.id ?? null,
      }, targetUserId);
      await refresh(selectedKey, tab);
      loadList();
      setFlash(`Tallennettu uusi versio v${data.version_no}.`);
    } catch (err) {
      if (err?.response?.status === 409) setConflict(true);
      setError(errMsg(err, 'Tallennus epäonnistui.'));
    } finally {
      setSaving(false);
    }
  };

  // Ctrl+S / Cmd+S saves a new version.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (canEdit && dirty && !validationError && !saving) save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const runAction = async (fn, successMsg, keepTab) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh(selectedKey, keepTab);
      loadList();
      if (successMsg) setFlash(successMsg);
    } catch (err) {
      setError(errMsg(err, 'Toiminto epäonnistui.'));
    } finally {
      setBusy(false);
    }
  };

  const createPersonal = () => runAction(
    () => createLlmPersonalPrompt(selectedKey, targetUserId),
    'Oma versio luotu yleisestä.',
    'personal'
  );

  const togglePersonalActive = (isActive) => runAction(
    () => setLlmPersonalPromptActive(selectedKey, isActive, targetUserId),
    isActive ? 'Generointi käyttää nyt omaa versiota.' : 'Generointi käyttää nyt yleistä versiota.',
    tab
  );

  const merge = async (decisions) => {
    setMergeSaving(true);
    setMergeError(null);
    try {
      const { data } = await mergeLlmPromptFromUniversal(selectedKey, decisions, state.personal.id, targetUserId);
      setMergeOpen(false);
      await refresh(selectedKey, 'personal');
      loadList();
      setFlash(`Oma versio päivitetty (v${data.version_no}).`);
    } catch (err) {
      setMergeError(errMsg(err, 'Päivitys epäonnistui.'));
    } finally {
      setMergeSaving(false);
    }
  };

  const selectReportType = (type) => {
    if (type === reportType || !confirmDiscard()) return;
    setReportType(type);
  };

  // "Delete" = archive: hidden from the dashboard and from generation, history kept.
  const archive = async (target) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = target.kind === 'group'
        ? await archiveLlmPromptGroup(target.value)
        : await archiveLlmPrompt(target.value);
      setConfirmArchive(null);
      if (data.archived.includes(selectedKey)) { setSelectedKey(null); setState(null); }
      await loadList();
      setFlash(`Poistettu: ${data.archived.join(', ')}${data.kept?.length ? ` (ei poistettu: ${data.kept.join(', ')} — generointi tarvitsee)` : ''}. Palautettavissa Poistetut-listasta.`);
    } catch (err) {
      setError(errMsg(err, 'Poisto epäonnistui.'));
    } finally {
      setBusy(false);
    }
  };

  const restore = async (key) => {
    setBusy(true);
    try {
      await restoreLlmPrompt(key);
      await loadList();
      setFlash(`Palautettu: ${key}.`);
    } catch (err) {
      setError(errMsg(err, 'Palautus epäonnistui.'));
    } finally {
      setBusy(false);
    }
  };

  const updateReportTypes = (types) => runAction(
    () => setLlmPromptReportTypes(selectedKey, types),
    types.length ? `Raporttityypit päivitetty.` : 'Prompti on nyt yleinen (kaikille raporttityypeille).',
    tab
  );

  // Copy one heading section; also put it on the system clipboard as text.
  const copyGroup = (group) => {
    const clip = {
      heading: group.heading,
      rows: group.rows.map(r => r.content),
      from: state?.prompt?.name ?? selectedKey,
    };
    setClipboard(clip);
    storageSet(CLIPBOARD_KEY, clip, true);
    const text = `${clip.heading}\n${clip.rows.map(r => `- ${r}`).join('\n')}`;
    navigator.clipboard?.writeText(text).catch(() => {});
    setFlash(`Kopioitu: ${clip.heading} (${clip.rows.length} riviä). Liitä se toisen promptin editorissa.`);
  };

  // Pasted rows are new rows of this prompt (items belong to one prompt).
  const pasteClipboard = () => {
    if (!clipboard) return;
    setDraftRows(rows => [
      ...rows,
      ...clipboard.rows.map(content => ({ key: newRowKey(), item_id: null, heading: clipboard.heading, content })),
    ]);
    setResetToken(t => t + 1);
  };

  const groupedPrompts = useMemo(() => {
    const groups = new Map();
    for (const p of prompts || []) {
      if (!groups.has(p.group_name)) groups.set(p.group_name, []);
      groups.get(p.group_name).push(p);
    }
    return [...groups.entries()];
  }, [prompts]);

  if (!prompts) {
    return <div className="text-center mt-5"><Spinner animation="border" /></div>;
  }

  return (
    <div className="container-fluid py-3" style={{ maxWidth: 1400, paddingBottom: canEdit ? 110 : undefined }}>
      <div className="d-flex align-items-end flex-wrap gap-3 mb-4 pb-3 border-bottom">
        <div>
          <h4 className="mb-1" style={{ fontWeight: 600 }}>LLM-promptit</h4>
          <div className="text-muted" style={{ fontSize: '0.85rem' }}>Tekstien generoinnin ohjeet. Jokainen tallennus on uusi versio, vanhat säilyvät.</div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <label htmlFor="llm-report-type" className="text-muted small mb-0">Raporttityyppi:</label>
          <select
            id="llm-report-type"
            className="form-select form-select-sm"
            style={{ width: 300 }}
            value={reportType}
            onChange={(e) => selectReportType(e.target.value)}
          >
            <option value="">Kaikki raporttityypit</option>
            {reportTypes.map(t => <option key={t.type} value={t.type}>{t.name}</option>)}
          </select>
        </div>
        {isAdmin && (
          <div className="ms-auto d-flex align-items-center gap-2">
            <label htmlFor="llm-target-user" className="text-muted small mb-0">Omat promptit käyttäjälle:</label>
            <select
              id="llm-target-user"
              className="form-select form-select-sm"
              style={{ width: 240 }}
              value={targetUserId ?? ''}
              onChange={(e) => selectUser(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Minä{currentUser?.nimi ? ` (${currentUser.nimi})` : ''}</option>
              {users.filter(u => u.id !== currentUser?.id).map(u => (
                <option key={u.id} value={u.id}>{u.nimi} ({u.rooli})</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {targetName && (
        <div className="alert alert-info py-2" style={{ fontSize: '0.85rem' }}>
          Katselet käyttäjän <strong>{targetName}</strong> omia prompteja. Oma-välilehden muutokset tallentuvat hänen versioikseen.
        </div>
      )}

      <div className="row g-3">
        <div className="col-lg-3">
          {groupedPrompts.map(([group, items]) => (
            <div key={group} className="mb-4">
              <div className="llmp-nav-group-row">
                <div className="llmp-nav-group">{group}</div>
                {isAdmin && (confirmArchive?.kind === 'group' && confirmArchive.value === group ? (
                  <span className="d-flex gap-1 ms-auto">
                    <button type="button" className="btn btn-sm btn-danger py-0 px-2" style={{ fontSize: '0.72rem' }} disabled={busy} onClick={() => archive(confirmArchive)}>Poista ryhmä</button>
                    <button type="button" className="btn btn-sm btn-link py-0 px-1" style={{ fontSize: '0.72rem' }} onClick={() => setConfirmArchive(null)}>Peru</button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="llmp-icon danger ms-auto llmp-group-delete"
                    title={`Poista koko ryhmä "${group}" (${items.length} promptia)`}
                    onClick={() => setConfirmArchive({ kind: 'group', value: group })}
                  >
                    <i className="bi bi-trash3" />
                  </button>
                ))}
              </div>
              <div className="d-flex flex-column gap-1">
                {items.map(p => {
                  const active = p.prompt_key === selectedKey;
                  const inUsePersonal = p.in_use === 'personal';
                  return (
                    <button
                      key={p.prompt_key}
                      type="button"
                      className={`llmp-nav-item ${active ? 'active' : ''}`}
                      onClick={() => selectPrompt(p.prompt_key)}
                    >
                      <span className="text-truncate">{p.name}</span>
                      {p.personal?.has_universal_update && (
                        <i className="bi bi-exclamation-circle-fill text-warning" title="Yleistä on päivitetty oman version jälkeen" />
                      )}
                      <span className={`llmp-nav-badge ${inUsePersonal ? 'personal' : ''}`}>
                        {inUsePersonal ? `Oma v${p.personal.version_no}` : `v${p.universal?.version_no ?? '–'}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {groupedPrompts.length === 0 && <div className="text-muted small">Ei prompteja tälle raporttityypille.</div>}

          {isAdmin && archived.length > 0 && (
            <div className="mt-2">
              <button type="button" className="btn btn-sm btn-link p-0 text-muted" onClick={() => setShowArchived(v => !v)}>
                <i className={`bi bi-chevron-${showArchived ? 'down' : 'right'} me-1`} />Poistetut promptit ({archived.length})
              </button>
              {showArchived && (
                <div className="d-flex flex-column gap-1 mt-1">
                  {archived.map(p => (
                    <div key={p.prompt_key} className="llmp-nav-item" style={{ cursor: 'default', opacity: 0.8 }}>
                      <span className="text-truncate">{p.name} <span className="text-muted small">· {p.group_name}</span></span>
                      <button type="button" className="btn btn-sm btn-outline-secondary py-0 ms-auto" disabled={busy} onClick={() => restore(p.prompt_key)}>Palauta</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="col-lg-9">
          {loadingState && !state && <Spinner animation="border" />}
          {error && !state && <div className="alert alert-danger">{error}</div>}

          {state && (
            <div className="d-flex flex-column gap-3" style={{ opacity: loadingState ? 0.6 : 1 }}>
              <div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h5 className="mb-0" style={{ fontWeight: 600 }}>{state.prompt.name}</h5>
                  <span className="text-muted" style={{ fontSize: '0.78rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{state.prompt.prompt_key}</span>
                  {!isGlobal && (
                    // Which version generation uses for this user: universal or their own copy.
                    <div className="llmp-use ms-auto" role="radiogroup" aria-label="Generoinnissa käytetään">
                      <span className="llmp-use-label">Generoinnissa käytetään:</span>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={state.in_use !== 'personal'}
                        className={`llmp-use-opt ${state.in_use !== 'personal' ? 'active' : ''}`}
                        disabled={busy || dirty || state.in_use !== 'personal'}
                        title={dirty ? 'Tallenna tai peru muutokset ensin' : 'Käytä yleistä versiota'}
                        onClick={() => togglePersonalActive(false)}
                      >
                        Yleinen v{state.universal?.version_no}
                      </button>
                      {state.personal ? (
                        <button
                          type="button"
                          role="radio"
                          aria-checked={state.in_use === 'personal'}
                          className={`llmp-use-opt ${state.in_use === 'personal' ? 'active personal' : ''}`}
                          disabled={busy || dirty || state.in_use === 'personal'}
                          title={dirty ? 'Tallenna tai peru muutokset ensin' : 'Käytä omaa versiota'}
                          onClick={() => togglePersonalActive(true)}
                        >
                          Oma v{state.personal.version_no}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="llmp-use-opt"
                          disabled={busy || dirty}
                          title="Omaa versiota ei ole vielä. Luo se kopioimalla yleinen versio."
                          onClick={createPersonal}
                        >
                          <i className="bi bi-plus-lg me-1" />Luo oma
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {state.prompt.description && <div className="text-muted mt-1" style={{ fontSize: '0.85rem' }}>{state.prompt.description}</div>}
                <div className="d-flex align-items-center gap-2 flex-wrap mt-2" style={{ fontSize: '0.8rem' }}>
                  <span className="text-muted">Raporttityypit:</span>
                  {(state.prompt.report_types || []).length === 0 && <span className="llmp-pill">Yleinen: kaikki raporttityypit</span>}
                  {(state.prompt.report_types || []).map(t => (
                    <span key={t} className="llmp-pill">
                      {reportTypes.find(r => r.type === t)?.name || t}
                      {isAdmin && (
                        <button type="button" className="btn btn-link p-0 ms-1 text-muted" style={{ fontSize: '0.8rem', lineHeight: 1 }} title="Poista raporttityyppi" disabled={busy}
                          onClick={() => updateReportTypes(state.prompt.report_types.filter(x => x !== t))}>
                          <i className="bi bi-x" />
                        </button>
                      )}
                    </span>
                  ))}
                  {isAdmin && (
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 220, fontSize: '0.78rem' }}
                      value={addingType}
                      disabled={busy}
                      onChange={(e) => {
                        const t = e.target.value;
                        setAddingType('');
                        if (t) updateReportTypes([...(state.prompt.report_types || []), t]);
                      }}
                      aria-label="Lisää raporttityyppi"
                    >
                      <option value="">+ Lisää raporttityyppi…</option>
                      {reportTypes.filter(r => !(state.prompt.report_types || []).includes(r.type)).map(r => (
                        <option key={r.type} value={r.type}>{r.name}</option>
                      ))}
                    </select>
                  )}
                  {isAdmin && !PROTECTED_KEYS.includes(state.prompt.prompt_key) && (
                    confirmArchive?.kind === 'prompt' && confirmArchive.value === state.prompt.prompt_key ? (
                      <span className="ms-auto d-flex gap-1 align-items-center">
                        <span className="text-danger">Poistetaanko prompti? Historia säilyy ja sen voi palauttaa.</span>
                        <button type="button" className="btn btn-sm btn-danger py-0" disabled={busy} onClick={() => archive(confirmArchive)}>Poista</button>
                        <button type="button" className="btn btn-sm btn-link py-0" onClick={() => setConfirmArchive(null)}>Peru</button>
                      </span>
                    ) : (
                      <button type="button" className="btn btn-sm btn-outline-danger py-0 ms-auto" disabled={busy || dirty}
                        title={dirty ? 'Tallenna tai peru muutokset ensin' : 'Poista prompti (arkistoi)'}
                        onClick={() => setConfirmArchive({ kind: 'prompt', value: state.prompt.prompt_key })}>
                        <i className="bi bi-trash3 me-1" />Poista prompti
                      </button>
                    )
                  )}
                </div>
                {state.prompt.required_variables?.length > 0 && (
                  <div className="small mt-1">
                    Pakolliset muuttujat:{' '}
                    {state.prompt.required_variables.map(v => <code key={v} className="me-1">{`{{${v}}}`}</code>)}
                    <span className="text-muted">— täytetään generoinnissa automaattisesti.</span>
                  </div>
                )}
              </div>

              {!isGlobal && (
                <div className="llmp-tabs" role="tablist">
                  {[
                    ['universal', 'Yleinen', state.universal?.version_no],
                    ['personal', 'Oma versio', state.personal?.version_no],
                  ].map(([key, label, no]) => (
                    <button key={key} type="button" role="tab" aria-selected={tab === key} className={`llmp-tab ${tab === key ? 'active' : ''}`} onClick={() => selectTab(key)}>
                      {label}
                      {no && <span className="llmp-nav-badge">v{no}</span>}
                      {key === 'personal' && state.notices.length > 0 && <i className="bi bi-exclamation-circle-fill text-warning ms-1" />}
                    </button>
                  ))}
                </div>
              )}

              {flash && <div className="alert alert-success py-2 mb-0" style={{ fontSize: '0.85rem' }}>{flash}</div>}

              {tab === 'universal' && !isAdmin && (
                <div className="alert alert-light border py-2 mb-0" style={{ fontSize: '0.85rem' }}>
                  Vain ylläpitäjä voi muokata yleistä promptia. Voit tehdä oman version Oma-välilehdellä.
                </div>
              )}

              {tab === 'personal' && !state.personal && (
                <div className="border rounded bg-white p-4 text-center">
                  <p className="mb-2">{targetName ? `Käyttäjällä ${targetName} ei ole omaa versiota tästä promptista.` : 'Sinulla ei ole omaa versiota tästä promptista.'}</p>
                  <p className="text-muted small">Oma versio on kopio yleisestä. Sen jälkeen se muuttuu vain, kun sitä itse muokataan.</p>
                  <button type="button" className="btn btn-success" disabled={busy} onClick={createPersonal}>
                    {busy ? 'Luodaan…' : 'Luo oma versio yleisestä'}
                  </button>
                </div>
              )}

              {tab === 'personal' && state.personal && (
                <>
                  <Form.Check
                    type="switch"
                    id="llm-personal-active"
                    disabled={busy || dirty}
                    checked={!!state.personal.is_active}
                    onChange={(e) => togglePersonalActive(e.target.checked)}
                    label={<span className="small">Käytä omaa versiota generoinnissa{!state.personal.is_active && <span className="text-muted"> (nyt käytössä yleinen)</span>}</span>}
                  />
                  {state.notices.length > 0 && (
                    <div className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-0" style={{ fontSize: '0.85rem' }}>
                      <i className="bi bi-exclamation-triangle-fill" />
                      <span>Yleistä promptia on muutettu oman versiosi jälkeen ({state.notices.length} muutosta).</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-warning ms-auto"
                        disabled={dirty}
                        title={dirty ? 'Tallenna tai peru muutokset ensin' : ''}
                        onClick={() => { setMergeError(null); setMergeOpen(true); }}
                      >
                        Käy muutokset läpi
                      </button>
                    </div>
                  )}
                </>
              )}

              {current && (
                <>
                  <PromptRowEditor
                    rows={draftRows}
                    onChange={setDraftRows}
                    readOnly={!canEdit}
                    requiredVariables={state.prompt.required_variables}
                    globals={isGlobal ? null : state.globals}
                    excluded={draftExcluded}
                    onExcludedChange={setDraftExcluded}
                    resetToken={resetToken}
                    onCopyGroup={copyGroup}
                    clipboard={canEdit ? clipboard : null}
                    onPaste={pasteClipboard}
                  />

                  <div>
                    <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setShowPreview(s => !s)}>
                      <i className={`bi bi-chevron-${showPreview ? 'down' : 'right'}`} /> Esikatsele koottu teksti
                    </button>
                    {showPreview && (
                      <pre className="border rounded bg-light p-2 mt-1 mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.78rem', maxHeight: 400, overflow: 'auto' }}>
                        {previewText}
                      </pre>
                    )}
                  </div>

                  {canEdit && createPortal(
                    // Floating save bar, always on screen while the prompt is editable. Rendered into
                    // <body>: the page sits inside ZoomWrapper, whose CSS transform would otherwise make
                    // position: fixed relative to the page instead of the screen.
                    <div className={`llmp-savebar ${dirty || saving ? 'dirty' : 'clean'}`} role="region" aria-label="Tallennus">
                      <div className="llmp-savebar-status">
                        <i className={`bi ${dirty || saving ? 'bi-pencil-square' : 'bi-check2-circle'}`} />
                        <div>
                          <div className="fw-semibold">{dirty || saving ? 'Tallentamattomia muutoksia' : 'Ei tallentamattomia muutoksia'}</div>
                          <div className={`llmp-savebar-sub ${validationError || error ? 'error' : ''}`} title={error || validationError || undefined}>
                            {error || validationError || `${state.prompt.name} · ${tab === 'personal' ? 'oma versio' : 'yleinen'} · uusi versio v${(current?.version_no ?? 0) + 1}`}
                          </div>
                        </div>
                      </div>
                      <input
                        disabled={!dirty}
                        className="form-control form-control-sm llmp-savebar-note"
                        placeholder="Muutoksen kuvaus (valinnainen)"
                        maxLength={255}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        aria-label="Muutoksen kuvaus"
                      />
                      {conflict ? (
                        <button type="button" className="btn btn-sm llmp-savebar-cancel" onClick={() => refresh(selectedKey, tab)}>
                          Lataa uusin (hylkää muutokset)
                        </button>
                      ) : (
                        <button type="button" className="btn btn-sm llmp-savebar-cancel" disabled={saving || !dirty} onClick={() => resetDraft(current)}>
                          Peru muutokset
                        </button>
                      )}
                      <button type="button" className="btn llmp-savebar-save" disabled={!dirty || !!validationError || saving} onClick={save} title="Tallenna (Ctrl+S)">
                        <i className="bi bi-check2-circle me-1" />
                        {saving ? 'Tallennetaan…' : 'Tallenna uusi versio'}
                      </button>
                    </div>,
                    document.body
                  )}

                  {error && (
                    <div className="alert alert-danger py-2 mb-0 d-flex align-items-center gap-2" style={{ fontSize: '0.85rem' }}>
                      <span>{error}</span>
                      {conflict && (
                        <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={() => refresh(selectedKey, tab)}>
                          Lataa uusin versio (hylkää omat muutokset)
                        </button>
                      )}
                    </div>
                  )}

                  <PromptHistory
                    promptKey={selectedKey}
                    scope={tab}
                    userId={targetUserId}
                    currentVersionId={current.id}
                    canRestore={canEdit}
                    disabled={dirty}
                    onRestored={(item) => runAction(async () => {}, `Versio v${item.version_no} palautettu uusimmaksi versioksi.`, tab)}
                  />
                </>
              )}

              {!isGlobal && <PromptExamples promptKey={selectedKey} isAdmin={isAdmin} defaultReportType={reportType || state.prompt.report_types?.[0] || ''} />}
            </div>
          )}
        </div>
      </div>

      {state?.notices && (
        <MergeDialog
          show={mergeOpen}
          notices={state.notices}
          saving={mergeSaving}
          error={mergeError}
          onCancel={() => setMergeOpen(false)}
          onConfirm={merge}
        />
      )}
    </div>
  );
}
